import {
  Card,
  CardBody,
  Row,
  Col,
  Breadcrumb,
  BreadcrumbItem,
  FormGroup,
  Input,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Button,
} from "reactstrap";
import "moment-timezone";
import { useState, useEffect, useContext } from "react";
import {
  NavLink as DomNavLink,
  useHistory,
  useLocation,
} from "react-router-dom";
import LoadingOverlay from "react-loading-overlay";
import { MainContext } from "context/MainContext.js";
import { faHome } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Utilities from "components/Utils/utilities";
import ImageGallery from "react-image-gallery";
import ChartComponent from "components/Utils/Chart";
LoadingOverlay.propTypes = undefined;
import Switch from "react-bootstrap-switch";
import "react-toastify/dist/ReactToastify.css";

const RoomDetails = () => {
  const history = useHistory();
  const location = useLocation();
  const privileges = useContext(MainContext);
  const emptyValues = [{ labels: 0, powerDBs: 0 }];

  const [roomId, setRoomId] = useState(
    location.state ? location.state.roomId : "",
  );

  const [siteId, setSiteId] = useState(
    location.state ? location.state.siteId : "",
  );

  const [images, setImages] = useState([]);

  const [details, setDetails] = useState(null);
  const [memo, setMemo] = useState("");
  const [saveStatus, setSaveStatus] = useState("");
  const [userUpdate, setUserUpdate] = useState(false);
  const [horizontalTabs, setHorizontalTabs] = useState("details");
  const [visitedTabs, setVisitedTabs] = useState(new Set());
  const [current, setCurrent] = useState(false);
  const [t0, setT0] = useState(true);
  const [t4, setT4] = useState(true);
  const [activeT0, setActiveT0] = useState(false);
  const [activeT4, setActiveT4] = useState(false);

  const PSD_NOISE_OFFSET = 48.75;

  useEffect(() => {
    if (!Utilities.hasAccess(privileges["installations.site.edit"])) {
      history.push({
        pathname: "/",
      });
    } else if (Utilities.hasAgreedTerms() == "false") {
      history.push({
        pathname: "/auth/terms",
      });
    }

    fetchData();
  }, []);

  useEffect(() => {
    if (!visitedTabs.has(horizontalTabs)) {
      if (horizontalTabs == "performances") {
        loadData();
      }
      setVisitedTabs(new Set(visitedTabs).add(horizontalTabs));
    }
  }, [horizontalTabs]);

  const fetchData = () => {
    fetch("/api/v1/site/room/details", {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        sessionId: sessionStorage.getItem("sessionId"),
        roomId: roomId,
      },
    })
      .then((response) => response.json())
      .then((actualData) => {
        setDetails(actualData.data);
        setMemo(actualData.extraData);
        setImages(actualData.data.images);
        setActiveT0(actualData.data.activeT0);
        if (!actualData.data.activeT0) {
          setT0(false);
        }

        setActiveT4(actualData.data.activeT1T4);
        if (!actualData.data.activeT1T4) {
          setT4(false);
        }

        if (!actualData.data.activeT0 && !actualData.data.activeT1T4) {
          setCurrent(true);
        }
      })
      .catch((err) => {
        console.log(err.message);
      });
  };

  const loadData = async () => {
    await manageData("upstream", "snrProbe");
    await manageData("downstream", "snrProbe");
    await manageData("upstream", "psdRx");
    await manageData("downstream", "psdRx");
    await manageData("upstream", "noise");
    await manageData("downstream", "noise");
  };

  const getLineColor = (item, index) => {
    if (item.tier == null) {
      if (index == 0) {
        return "rgb(76, 212, 88)";
      } else {
        return "rgb(212, 203, 76)";
      }
    } else if (item.tier == 0) {
      return "rgb(0, 0, 0)";
    } else if (item.tier > 0) {
      return "rgb(72, 93, 218)";
    }
  };

  const getLineWidth = (item) => {
    if (item.tier == null) {
      return 0.8;
    } else if (item.tier == 0) {
      return 5;
    } else if (item.tier > 0) {
      return 0.8;
    }
  };

  const getLineOrder = (item) => {
    if (item.tier == null) {
      return 0;
    } else if (item.tier == 0) {
      return 10;
    } else if (item.tier > 0) {
      return 5;
    }
  };

  const manageData = async (direction, type) => {
    let hasError = false;
    let result = null;

    try {
      result = await fetchSpectrum(direction, type);
      hasError = result.status != 200;
    } catch (error) {
      hasError = true;
    }

    // Mapping
    const dataSetters = {
      upstream: {
        snrProbe: [
          setUpstreamSNR,
          setUpstreamSNRStatus,
          setUpstreamSNRMaxPowerDb,
          setUpstreamSNRDataSet,
        ],
        psdRx: [
          setUpstreamPSDData,
          setUpstreamPSDStatus,
          setUpstreamPSDMaxPowerDb,
          setUpstreamPSDDataSet,
        ],
        noise: [
          setUpstreamNoiseData,
          setUpstreamNoiseStatus,
          setUpstreamNoiseMaxPowerDb,
          setUpstreamNoiseDataSet,
        ],
      },
      downstream: {
        snrProbe: [
          setDownstreamSNR,
          setDownstreamSNRStatus,
          setDownstreamSNRMaxPowerDb,
          setDownstreamSNRDataSet,
        ],
        psdRx: [
          setDownstreamPSDData,
          setDownstreamPSDStatus,
          setDownstreamPSDMaxPowerDb,
          setDownstreamPSDDataSet,
        ],
        noise: [
          setDownstreamNoiseData,
          setDownstreamNoiseStatus,
          setDownstreamNoiseMaxPowerDb,
          setDownstreamNoiseDataSet,
        ],
      },
    };

    if (dataSetters[direction] && dataSetters[direction][type]) {
      const [setData, setStatus, setMaxPowerDB, setDataSet] =
        dataSetters[direction][type];

      if (hasError) {
        setData(emptyValues);
        setMaxPowerDB(null);
        setStatus("ok");
      } else {
        setData(result.data);
        setMaxPowerDB(result.data[0]?.maxPowerDB);
        setStatus("ok");

        if (type === "snrProbe") {
          const seen = new Set();
          const agcValues = result.data
            .filter((item) => {
              if (seen.has(item.tier)) return false;
              seen.add(item.tier);
              return true;
            })
            .map((item) => ({
              tier: item.tier,
              label: getTierLabel(item.tier),
              value: item.agcDb,
            }));

          if (direction === "upstream") setUpstreamAgcDB(agcValues);
          if (direction === "downstream") setDownstreamAgcDB(agcValues);
        }

        const yAxisConfig = getYAxisConfig(type);

        setDataSet({
          data: {
            labels: result.data[0]?.labels || [],
            datasets: result.data.map((item, index) => ({
              borderColor: getLineColor(item, index),
              pointRadius: 0,
              fill: true,
              borderWidth: getLineWidth(item),
              data: transformPowerDBs(item?.powerDBs || [], type),
              order: getLineOrder(item),
            })),
          },
          options: {
            plugins: {
              legend: {
                display: false,
              },
              tooltip: {
                enabled: true,
              },
            },
            scales: {
              y: {
                min: yAxisConfig.min,
                max: yAxisConfig.max,
                title: {
                  display: true,
                  text: yAxisConfig.label,
                },
              },
              x: {
                type: "linear",
                title: { display: true, text: "MHz" },
                ticks: {
                  callback: function (value) {
                    return parseFloat(value).toFixed(0);
                  },
                },
              },
            },
            animation: false,
          },
        });
      }
    }
  };

  const getYAxisConfig = (type) => {
    if (type === "noise" || type === "psdRx") {
      return { label: "dBmV (1 Hz)", min: -120, max: -20 };
    }
    return { label: "dB", min: 0, max: 60 };
  };

  const transformPowerDBs = (powerDBs, type) => {
    if (type === "noise" || type === "psdRx") {
      return powerDBs.map((value) => value + PSD_NOISE_OFFSET);
    }
    return powerDBs;
  };

  const getAgcColor = (tier) => {
    if (tier === null)
      return "linear-gradient(to right, rgb(76, 212, 88), rgb(212, 203, 76))";
    if (tier === 0) return "rgb(0, 0, 0)";
    return "rgb(72, 93, 218)";
  };

  const getTierLabel = (tier) => {
    if (tier === null) return "Current";
    if (tier === 0) return "T0";
    if (tier === 1) return "T1";
    if (tier === 2) return "T2";
    if (tier === 3) return "T3";
    if (tier === 4) return "T4";
    return "";
  };

  const fetchSpectrum = async (direction, type) => {
    try {
      const response = await fetch(`/api/v1/endpoint/spectrum`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          sessionId: sessionStorage.getItem("sessionId"),
          macAddress: details?.macAddress || "",
          direction: direction,
          type: type,
          averaging: "avg4",
          current: current,
          t0: t0,
          t4: t4,
        },
      });
      return await response.json();
    } catch (err) {
      console.log(err.message);
    }
  };

  const showOverlayMessage = (status) => {
    if (status == "loading") {
      return "Loading Data";
    } else {
      return "";
    }
  };

  // AGC DB
  const [upstreamAgcDB, setUpstreamAgcDB] = useState([]);
  const [downstreamAgcDB, setDownstreamAgcDB] = useState([]);

  // SNR
  const [upstreamSNRStatus, setUpstreamSNRStatus] = useState("loading");
  const [upstreamSNR, setUpstreamSNR] = useState(emptyValues);
  const [upstreamSNRMaxPowerDb, setUpstreamSNRMaxPowerDb] = useState(null);
  const [downstreamSNRStatus, setDownstreamSNRStatus] = useState("loading");
  const [downstreamSNR, setDownstreamSNR] = useState(emptyValues);
  const [downstreamSNRMaxPowerDb, setDownstreamSNRMaxPowerDb] = useState(null);
  const [upstreamSNRDataSet, setUpstreamSNRDataSet] = useState({
    data: {
      labels: upstreamSNR.map((item) => item.FrequencyMHz),
      datasets: [
        {
          borderColor: "rgb(116, 209, 34)",
          pointRadius: 0,
          fill: true,
          data: upstreamSNR.map((item) => item.powerDBs),
        },
      ],
    },
    options: {
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: true,
        },
      },
      scales: {
        y: {
          title: {
            display: true,
            text: "dB",
            min: 0,
            max: 60,
          },
        },
        x: {
          title: {
            display: true,
            text: "MHz",
          },
          ticks: {
            callback: function (value) {
              return parseFloat(value).toFixed(0);
            },
          },
        },
      },
      animation: false,
    },
  });
  const [downstreamSNRDataSet, setDownstreamSNRDataSet] = useState({
    data: {
      labels: downstreamSNR.map((item) => item.FrequencyMHz),
      datasets: [
        {
          borderColor: "rgb(116, 209, 34)",
          pointRadius: 0,
          fill: true,
          data: downstreamSNR.map((item) => item.powerDBs),
        },
      ],
    },
    options: {
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: true,
        },
      },
      scales: {
        y: {
          title: {
            display: true,
            text: "dB",
            min: 0,
            max: 60,
          },
        },
        x: {
          title: {
            display: true,
            text: "MHz",
          },
          ticks: {
            callback: function (value) {
              return parseFloat(value).toFixed(0);
            },
          },
        },
      },
    },
  });

  // PSD
  const [upstreamPSDStatus, setUpstreamPSDStatus] = useState("loading");
  const [upstreamPSDData, setUpstreamPSDData] = useState(emptyValues);
  const [upstreamPSDMaxPowerDb, setUpstreamPSDMaxPowerDb] = useState(null);
  const [downstreamPSDStatus, setDownstreamPSDStatus] = useState("loading");
  const [downstreamPSDData, setDownstreamPSDData] = useState(emptyValues);
  const [downstreamPSDMaxPowerDb, setDownstreamPSDMaxPowerDb] = useState(null);
  const [upstreamPSDDataSet, setUpstreamPSDDataSet] = useState({
    data: {
      labels: upstreamPSDData.map((item) => item.FrequencyMHz),
      datasets: [
        {
          borderColor: "rgb(116, 209, 34)",
          pointRadius: 0,
          fill: true,
          data: upstreamPSDData.map((item) => item.powerDBs),
        },
      ],
    },
    options: {
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: true,
        },
      },
      scales: {
        y: {
          title: {
            display: true,
            text: "dBmV (1 Hz)",
            min: -120,
            max: -20,
          },
        },
        x: {
          title: {
            display: true,
            text: "MHz",
          },
          ticks: {
            callback: function (value) {
              return parseFloat(value).toFixed(0);
            },
          },
        },
      },
    },
  });
  const [downstreamPSDDataSet, setDownstreamPSDDataSet] = useState({
    data: {
      labels: downstreamPSDData.map((item) => item.FrequencyMHz),
      datasets: [
        {
          borderColor: "rgb(116, 209, 34)",
          pointRadius: 0,
          fill: true,
          data: downstreamPSDData.map((item) => item.powerDBs),
        },
      ],
    },
    options: {
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: true,
        },
      },
      scales: {
        y: {
          title: {
            display: true,
            text: "dBmV (1 Hz)",
            min: -120,
            max: -20,
          },
        },
        x: {
          title: {
            display: true,
            text: "MHz",
          },
          ticks: {
            callback: function (value) {
              return parseFloat(value).toFixed(0);
            },
          },
        },
      },
    },
  });

  // Noise
  const [upstreamNoiseStatus, setUpstreamNoiseStatus] = useState("loading");
  const [upstreamNoiseData, setUpstreamNoiseData] = useState(emptyValues);
  const [upstreamNoiseMaxPowerDb, setUpstreamNoiseMaxPowerDb] = useState(null);
  const [downstreamNoiseStatus, setDownstreamNoiseStatus] = useState("loading");
  const [downstreamNoiseData, setDownstreamNoiseData] = useState(emptyValues);
  const [downstreamNoiseMaxPowerDb, setDownstreamNoiseMaxPowerDb] =
    useState(null);
  const [upstreamNoiseDataSet, setUpstreamNoiseDataSet] = useState({
    data: {
      labels: upstreamNoiseData.map((item) => item.FrequencyMHz),
      datasets: [
        {
          borderColor: "rgb(116, 209, 34)",
          pointRadius: 0,
          fill: true,
          data: upstreamNoiseData.map((item) => item.powerDBs),
        },
      ],
    },
    options: {
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: true,
        },
      },
      scales: {
        y: {
          title: {
            display: true,
            text: "dBmV (1 Hz)",
            min: -120,
            max: -20,
          },
        },
        x: {
          title: {
            display: true,
            text: "MHz",
          },
          ticks: {
            callback: function (value) {
              return parseFloat(value).toFixed(0);
            },
          },
        },
      },
    },
  });
  const [downstreamNoiseDataSet, setDownstreamNoiseDataSet] = useState({
    data: {
      labels: downstreamNoiseData.map((item) => item.FrequencyMHz),
      datasets: [
        {
          borderColor: "rgb(116, 209, 34)",
          pointRadius: 0,
          fill: true,
          data: downstreamNoiseData.map((item) => item.powerDBs),
        },
      ],
    },
    options: {
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: true,
        },
      },
      scales: {
        y: {
          title: {
            display: true,
            text: "dBmV (1 Hz)",
            min: -120,
            max: -20,
          },
        },
        x: {
          title: {
            display: true,
            text: "MHz",
          },
          ticks: {
            callback: function (value) {
              return parseFloat(value).toFixed(0);
            },
          },
        },
      },
    },
  });

  useEffect(() => {
    if (userUpdate) {
      setSaveStatus("progress");
    }

    const timer = setTimeout(() => {
      if (userUpdate) {
        updateMemo();
        setUserUpdate(false);
      }
    }, 2000);
    return () => clearTimeout(timer);
  }, [memo]);

  const handleMemoChange = (e) => {
    const { name, value } = e.target;
    setMemo(value.toString());
    setUserUpdate(true);
  };

  const updateMemo = () => {
    fetch("/api/v1/site/room/details/update_memo", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        sessionId: sessionStorage.getItem("sessionId"),
      },
      body: JSON.stringify({
        roomId: roomId,
        memo: memo,
      }),
    })
      .then((response) => response.json())
      .then((actualData) => {
        if (actualData.status == 200) {
          setSaveStatus("saved");
        } else {
          setSaveStatus("error");
        }
      })
      .catch((err) => {
        setSaveStatus("error");
        console.log(err.message);
      });
  };

  const manageImages = () => {
    const location = {
      pathname: "/admin/installations/site/endpoint/details/images",
      state: {
        roomId: roomId ? roomId : "",
        siteId: siteId,
      },
    };
    history.push(location);
  };

  const loadInProgress = () => {
    if (
      upstreamSNRStatus == "loading" ||
      downstreamSNRStatus == "loading" ||
      upstreamPSDStatus == "loading" ||
      downstreamPSDStatus == "loading" ||
      upstreamNoiseStatus == "loading" ||
      downstreamNoiseStatus == "loading"
    ) {
      return true;
    }

    return false;
  };

  const refresh = () => {
    setUpstreamSNRStatus("loading");
    setDownstreamSNRStatus("loading");
    setUpstreamPSDStatus("loading");
    setDownstreamPSDStatus("loading");
    setUpstreamNoiseStatus("loading");
    setDownstreamNoiseStatus("loading");

    loadData();
  };

  const handleCurrentChanged = (e) => {
    const { props, state } = e;

    setCurrent(state.value);
  };

  const handleT0Changed = (e) => {
    const { props, state } = e;

    setT0(state.value);
  };

  const handleT4Changed = (e) => {
    const { props, state } = e;

    setT4(state.value);
  };

  return (
    <>
      <div className="content">
        <Row>
          <Col md="12">
            <Card>
              <CardBody>
                <Breadcrumb>
                  <BreadcrumbItem>
                    <DomNavLink
                      to={{
                        pathname: "/admin/dashboard",
                      }}
                    >
                      <FontAwesomeIcon icon={faHome} />
                    </DomNavLink>
                  </BreadcrumbItem>
                  <BreadcrumbItem>
                    <DomNavLink
                      to={{
                        pathname: "/admin/installations/site/list",
                      }}
                    >
                      Hotels
                    </DomNavLink>
                    <i>&lt;{details ? details.siteName : ""}&gt;</i>
                  </BreadcrumbItem>
                  <BreadcrumbItem>
                    <DomNavLink
                      to={{
                        pathname: "/admin/installations/site/details",
                        state: {
                          siteId: siteId,
                          status: "all",
                        },
                      }}
                    >
                      Endpoint
                    </DomNavLink>
                    <i>&lt;{details ? details.number : ""}&gt;</i>
                  </BreadcrumbItem>
                  <BreadcrumbItem>Details</BreadcrumbItem>
                </Breadcrumb>
              </CardBody>
            </Card>
          </Col>
        </Row>
        <Row>
          <Col>
            <Card
              className="h-100"
              style={{
                border:
                  details && details.isLocked ? "5px groove gold" : "none",
                borderRadius: details && details.isLocked ? "30px" : "0px",
              }}
            >
              <CardBody className="d-flex flex-column">
                <div
                  className="nav-tabs-navigation"
                  style={{ marginBottom: "5px" }}
                >
                  <div className="nav-tabs-wrapper">
                    <Nav id="tabs" role="tablist" tabs>
                      <NavItem>
                        <NavLink
                          aria-expanded={horizontalTabs === "details"}
                          data-toggle="tab"
                          href="#"
                          role="tab"
                          className={
                            horizontalTabs === "details" ? "active" : ""
                          }
                          style={{ color: "black" }}
                          onClick={() => setHorizontalTabs("details")}
                        >
                          Details
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          aria-expanded={horizontalTabs === "images"}
                          data-toggle="tab"
                          href="#"
                          role="tab"
                          className={
                            horizontalTabs === "images" ? "active" : ""
                          }
                          style={{ color: "black" }}
                          onClick={() => setHorizontalTabs("images")}
                        >
                          Images
                        </NavLink>
                      </NavItem>
                      {details && details.isProvisionned ? (
                        <NavItem>
                          <NavLink
                            aria-expanded={horizontalTabs === "performances"}
                            data-toggle="tab"
                            href="#"
                            role="tab"
                            className={
                              horizontalTabs === "performances" ? "active" : ""
                            }
                            style={{ color: "black" }}
                            onClick={() => setHorizontalTabs("performances")}
                          >
                            Performances
                          </NavLink>
                        </NavItem>
                      ) : (
                        ""
                      )}
                    </Nav>
                  </div>
                </div>
                <TabContent
                  id="my-tab-content"
                  activeTab={horizontalTabs}
                  style={{ paddingLeft: "20px" }}
                >
                  <TabPane tabId="details" role="tabpanel">
                    {details && details.isLocked ? (
                      ""
                    ) : (
                      <Row>
                        <Col>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "flex-end",
                              alignItems: "center",
                            }}
                          >
                            {false ? ( // To remove on Virtuoso 3.6.0
                              <UncontrolledDropdown direction="down">
                                <DropdownToggle
                                  caret
                                  color="info"
                                  style={{
                                    lineHeight: "0.5em",
                                    marginTop: "0px",
                                  }}
                                >
                                  Actions
                                </DropdownToggle>
                                <DropdownMenu container={"body"}>
                                  <DropdownItem>
                                    Replace Equipement
                                  </DropdownItem>
                                </DropdownMenu>
                              </UncontrolledDropdown>
                            ) : (
                              ""
                            )}
                          </div>
                        </Col>
                      </Row>
                    )}

                    <Row>
                      <Col>
                        <FormGroup>
                          <label>Number</label>
                          <p>{details?.number || ""}</p>
                        </FormGroup>
                      </Col>
                      <Col>
                        <FormGroup>
                          <label>MAC Address</label>
                          <p>{details?.macAddress || ""}</p>
                        </FormGroup>
                      </Col>
                      <Col>
                        <FormGroup>
                          <label>Profile</label>
                          <p>{details?.profileName || ""}</p>
                        </FormGroup>
                      </Col>
                      <Col>
                        <FormGroup>
                          <label>Status</label>
                          <p>{details?.status || ""}</p>
                        </FormGroup>
                      </Col>
                      <Col>
                        <FormGroup>
                          <label>Serial Number</label>
                          <p>{details?.serialNumber || ""}</p>
                        </FormGroup>
                      </Col>
                      <Col>
                        <FormGroup>
                          <label>Installation Status</label>
                          <p>{details?.installationStatus || ""}</p>
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row className="flex-grow-1" style={{ minHeight: 0 }}>
                      <Col>
                        <FormGroup className="d-flex flex-column h-100">
                          <label>Memo</label>
                          {details && details.isLocked ? (
                            memo
                          ) : (
                            <>
                              <Input
                                type="textarea"
                                name="memo"
                                className="flex-grow-1"
                                style={{
                                  resize: "none",
                                  padding: "10px",
                                  overflow: "hidden",
                                  minHeight: 0,
                                }}
                                maxLength={250}
                                onChange={handleMemoChange}
                                value={memo}
                              />
                              <span
                                style={{
                                  position: "absolute",
                                  bottom: "35px",
                                  right: "20px",
                                  fontSize: "20px",
                                }}
                              >
                                {saveStatus === "progress" && "⏳"}
                                {saveStatus === "saved" && "✔️"}
                                {saveStatus === "error" && "❌"}
                              </span>
                            </>
                          )}
                        </FormGroup>
                      </Col>
                    </Row>
                  </TabPane>
                  <TabPane tabId="images" role="tabpanel">
                    {details && details.isLocked ? (
                      ""
                    ) : (
                      <Row>
                        <Col>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "flex-end",
                              alignItems: "center",
                            }}
                          >
                            <UncontrolledDropdown direction="down">
                              <DropdownToggle
                                caret
                                color="info"
                                style={{
                                  lineHeight: "1.0em",
                                  marginTop: "0px",
                                }}
                              >
                                Actions
                              </DropdownToggle>
                              <DropdownMenu container={"body"}>
                                <DropdownItem onClick={() => manageImages()}>
                                  Manage Images
                                </DropdownItem>
                              </DropdownMenu>
                            </UncontrolledDropdown>
                          </div>
                        </Col>
                      </Row>
                    )}
                    <Row>
                      <Col>
                        {images && images.length > 0 ? (
                          <ImageGallery
                            items={images}
                            showThumbnails={true}
                            showPlayButton={false}
                            showNav={false}
                            autoPlay={false}
                            showFullscreenButton={true}
                            showIndex={true}
                            lazyLoad={true}
                            slideDuration={450}
                            additionalClass="hotel-gallery"
                          />
                        ) : (
                          "No Images Uploaded Yet"
                        )}
                      </Col>
                    </Row>
                  </TabPane>
                  {details && details.isProvisionned ? (
                    <TabPane tabId="performances" role="tabpanel">
                      <Row>
                        <Col>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "flex-end",
                            }}
                          >
                            <Row>
                              <Col>
                                <FormGroup
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                  }}
                                >
                                  <div
                                    style={{
                                      marginBottom: "8px",
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: "12px",
                                        height: "12px",
                                        background:
                                          "linear-gradient(to right, rgb(76, 212, 88), rgb(212, 203, 76))",
                                        marginRight: "4px",
                                      }}
                                    ></div>
                                    Current
                                  </div>
                                  <Switch
                                    name="current"
                                    offColor="success"
                                    offText={
                                      <i className="nc-icon nc-simple-remove" />
                                    }
                                    onColor="success"
                                    onText={
                                      <i className="nc-icon nc-check-2" />
                                    }
                                    onChange={handleCurrentChanged}
                                    value={current}
                                    disabled={loadInProgress()}
                                  />
                                </FormGroup>
                              </Col>

                              {activeT0 ? (
                                <Col>
                                  <FormGroup
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                    }}
                                  >
                                    <>
                                      <div
                                        style={{
                                          marginBottom: "8px",
                                          display: "flex",
                                          alignItems: "center",
                                        }}
                                      >
                                        <div
                                          style={{
                                            width: "12px",
                                            height: "12px",
                                            backgroundColor: "rgb(0, 0, 0)",
                                            marginRight: "4px",
                                          }}
                                        ></div>
                                        T0
                                      </div>
                                      <Switch
                                        name="compare"
                                        offColor="success"
                                        offText={
                                          <i className="nc-icon nc-simple-remove" />
                                        }
                                        onColor="success"
                                        onText={
                                          <i className="nc-icon nc-check-2" />
                                        }
                                        onChange={handleT0Changed}
                                        value={t0}
                                        disabled={loadInProgress()}
                                      />
                                    </>
                                  </FormGroup>
                                </Col>
                              ) : (
                                ""
                              )}

                              {activeT4 ? (
                                <Col>
                                  <FormGroup
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                    }}
                                  >
                                    <>
                                      <div
                                        style={{
                                          marginBottom: "8px",
                                          display: "flex",
                                          alignItems: "center",
                                        }}
                                      >
                                        <div
                                          style={{
                                            width: "12px",
                                            height: "12px",
                                            backgroundColor: "rgb(72, 93, 218)",
                                            marginRight: "4px",
                                          }}
                                        ></div>
                                        T1..T4
                                      </div>
                                      <Switch
                                        name="compare"
                                        offColor="success"
                                        offText={
                                          <i className="nc-icon nc-simple-remove" />
                                        }
                                        onColor="success"
                                        onText={
                                          <i className="nc-icon nc-check-2" />
                                        }
                                        onChange={handleT4Changed}
                                        value={t4}
                                        disabled={loadInProgress()}
                                      />
                                    </>
                                  </FormGroup>
                                </Col>
                              ) : (
                                ""
                              )}

                              <Col>
                                <Button
                                  onClick={() => refresh()}
                                  disabled={loadInProgress()}
                                  className="btn-round"
                                  color="info"
                                  type="submit"
                                >
                                  Refresh
                                </Button>
                              </Col>
                            </Row>
                          </div>
                        </Col>
                      </Row>
                      <Row>
                        <Col>
                          <FormGroup>
                            <label
                              style={{
                                color: "#333",
                                fontWeight: "600",
                                fontSize: "18px",
                              }}
                            >
                              AGC dB UPSTREAM
                            </label>
                            <div
                              style={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: "8px",
                                marginTop: "4px",
                              }}
                            >
                              {upstreamAgcDB.map((item) => (
                                <span
                                  key={item.tier}
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    backgroundColor: "#f5f5f5",
                                    border: "1px solid #e0e0e0",
                                    borderRadius: "6px",
                                    padding: "4px 10px",
                                    fontSize: "13px",
                                    color: "#333",
                                    fontWeight: "500",
                                  }}
                                >
                                  <span
                                    style={{
                                      width: "10px",
                                      height: "10px",
                                      borderRadius: "50%",
                                      background: getAgcColor(item.tier),
                                      display: "inline-block",
                                      flexShrink: 0,
                                    }}
                                  />
                                  {item.label}:{" "}
                                  {item.value !== null ? item.value : "-"}
                                </span>
                              ))}
                            </div>
                          </FormGroup>
                        </Col>
                        <Col>
                          <FormGroup>
                            <label
                              style={{
                                color: "#333",
                                fontWeight: "600",
                                fontSize: "18px",
                              }}
                            >
                              AGC dB DOWNSTREAM
                            </label>
                            <div
                              style={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: "8px",
                                marginTop: "4px",
                              }}
                            >
                              {downstreamAgcDB.map((item) => (
                                <span
                                  key={item.tier}
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    backgroundColor: "#f5f5f5",
                                    border: "1px solid #e0e0e0",
                                    borderRadius: "6px",
                                    padding: "4px 10px",
                                    fontSize: "13px",
                                    color: "#333",
                                    fontWeight: "500",
                                  }}
                                >
                                  <span
                                    style={{
                                      width: "10px",
                                      height: "10px",
                                      borderRadius: "50%",
                                      background: getAgcColor(item.tier),
                                      display: "inline-block",
                                      flexShrink: 0,
                                    }}
                                  />
                                  {item.label}:{" "}
                                  {item.value !== null ? item.value : "-"}
                                </span>
                              ))}
                            </div>
                          </FormGroup>
                        </Col>
                      </Row>
                      <Row>
                        <Col md="6">
                          <LoadingOverlay
                            active={upstreamSNRStatus !== "ok"}
                            spinner={upstreamSNRStatus === "loading"}
                            text={showOverlayMessage(upstreamSNRStatus)}
                          >
                            <ChartComponent
                              dataset={upstreamSNRDataSet}
                              title="Upstream SNR"
                              maxPowerDb={
                                Number.isFinite(Number(upstreamSNRMaxPowerDb))
                                  ? `${upstreamSNRMaxPowerDb} dB`
                                  : ""
                              }
                            />
                          </LoadingOverlay>
                        </Col>
                        <Col md="6">
                          <LoadingOverlay
                            active={downstreamSNRStatus !== "ok"}
                            spinner={downstreamSNRStatus === "loading"}
                            text={showOverlayMessage(downstreamSNRStatus)}
                          >
                            <ChartComponent
                              dataset={downstreamSNRDataSet}
                              title="Downstream SNR"
                              maxPowerDb={
                                Number.isFinite(Number(downstreamSNRMaxPowerDb))
                                  ? `${downstreamSNRMaxPowerDb} dB`
                                  : ""
                              }
                            />
                          </LoadingOverlay>
                        </Col>
                      </Row>
                      <Row>
                        <Col md="6">
                          <LoadingOverlay
                            active={upstreamPSDStatus !== "ok"}
                            spinner={upstreamPSDStatus === "loading"}
                            text={showOverlayMessage(upstreamPSDStatus)}
                          >
                            <ChartComponent
                              dataset={upstreamPSDDataSet}
                              title="Upstream PSD"
                              maxPowerDb={
                                Number.isFinite(Number(upstreamPSDMaxPowerDb))
                                  ? `${
                                      upstreamPSDMaxPowerDb + PSD_NOISE_OFFSET
                                    } dBmV (1 Hz)`
                                  : ""
                              }
                            />
                          </LoadingOverlay>
                        </Col>
                        <Col md="6">
                          <LoadingOverlay
                            active={downstreamPSDStatus !== "ok"}
                            spinner={downstreamPSDStatus === "loading"}
                            text={showOverlayMessage(downstreamPSDStatus)}
                          >
                            <ChartComponent
                              dataset={downstreamPSDDataSet}
                              title="Downstream PSD"
                              maxPowerDb={
                                Number.isFinite(Number(downstreamPSDMaxPowerDb))
                                  ? `${
                                      downstreamPSDMaxPowerDb + PSD_NOISE_OFFSET
                                    } dBmV (1 Hz)`
                                  : ""
                              }
                            />
                          </LoadingOverlay>
                        </Col>
                      </Row>
                      <Row>
                        <Col md="6">
                          <LoadingOverlay
                            active={upstreamNoiseStatus !== "ok"}
                            spinner={upstreamNoiseStatus === "loading"}
                            text={showOverlayMessage(upstreamNoiseStatus)}
                          >
                            <ChartComponent
                              dataset={upstreamNoiseDataSet}
                              title="Upstream Noise"
                              maxPowerDb={
                                Number.isFinite(Number(upstreamNoiseMaxPowerDb))
                                  ? `${
                                      upstreamNoiseMaxPowerDb + PSD_NOISE_OFFSET
                                    } dBmV (1 Hz)`
                                  : ""
                              }
                            />
                          </LoadingOverlay>
                        </Col>
                        <Col md="6">
                          <LoadingOverlay
                            active={downstreamNoiseStatus !== "ok"}
                            spinner={downstreamNoiseStatus === "loading"}
                            text={showOverlayMessage(downstreamNoiseStatus)}
                          >
                            <ChartComponent
                              dataset={downstreamNoiseDataSet}
                              title="Downstream Noise"
                              maxPowerDb={
                                Number.isFinite(
                                  Number(downstreamNoiseMaxPowerDb),
                                )
                                  ? `${
                                      downstreamNoiseMaxPowerDb +
                                      PSD_NOISE_OFFSET
                                    } dBmV (1 Hz)`
                                  : ""
                              }
                            />
                          </LoadingOverlay>
                        </Col>
                      </Row>
                    </TabPane>
                  ) : (
                    ""
                  )}
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default RoomDetails;
